import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getInstagramPostUrls, updateInstagramPosts } from '../services/api';
import { toast } from 'react-toastify';
import LoadingSpinner from '../components/LoadingSpinner';

const SettingsPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('homepage');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [instagramPosts, setInstagramPosts] = useState(['', '', '', '', '']); // 5 empty slots

    React.useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error('You must be logged in');
                navigate('/login');
                return;
            }

            const response = await getInstagramPostUrls(token);
            console.log('Settings loaded:', response);
            
            if (response.status === 'success' && response.data) {
                const posts = Array.isArray(response.data) ? [...response.data] : [];
                console.log('Loaded posts:', posts);
                
                // Fill remaining slots with empty strings to always have 5 slots
                while (posts.length < 5) {
                    posts.push('');
                }
                setInstagramPosts(posts.slice(0, 5));
            } else {
                // If no data, initialize with empty strings
                setInstagramPosts(['', '', '', '', '']);
            }
        } catch (error) {
            console.error('Error loading settings:', error);
            toast.error('Failed to load settings: ' + (error.message || 'Unknown error'));
            // Initialize with empty strings on error
            setInstagramPosts(['', '', '', '', '']);
        } finally {
            setLoading(false);
        }
    };

    const handleInstagramPostChange = (index, value) => {
        const newPosts = [...instagramPosts];
        newPosts[index] = value;
        setInstagramPosts(newPosts);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error('You must be logged in');
                navigate('/login');
                return;
            }

            // Filter out empty posts
            const validPosts = instagramPosts.filter(post => post && post.trim() !== '');
            console.log('Saving Instagram posts:', validPosts);
            
            if (validPosts.length === 0) {
                toast.warning('Please add at least one Instagram post URL');
                setSaving(false);
                return;
            }
            
            const response = await updateInstagramPosts(validPosts, token);
            console.log('Save response:', response);
            toast.success('Settings saved successfully!');
            
            // Reload settings to verify
            await loadSettings();
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error(error.message || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 py-8">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
                    <button
                        onClick={() => navigate('/admin/dashboard')}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                        Back to Dashboard
                    </button>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                    <div className="border-b border-gray-200">
                        <nav className="flex -mb-px">
                            <button
                                onClick={() => setActiveTab('homepage')}
                                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                                    activeTab === 'homepage'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                Homepage
                            </button>
                        </nav>
                    </div>
                </div>

                {/* Homepage Settings */}
                {activeTab === 'homepage' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8">
                        <div className="mb-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Homepage Settings</h2>
                            <p className="text-gray-600">
                                Configure Instagram posts and reels to display on the homepage
                            </p>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-900 mb-4">
                                    Top 5 Instagram Post/Reel URLs
                                </label>
                                <p className="text-sm text-gray-600 mb-4">
                                    Add up to 5 Instagram post or reel URLs. To get a URL: Go to the Instagram post/reel → Click three dots (⋮) → Copy link
                                    <br />
                                    <span className="text-xs text-gray-500 mt-2 block">
                                        Examples: 
                                        <code className="ml-1 px-1 py-0.5 bg-gray-100 rounded">https://www.instagram.com/p/ABC123xyz/</code> (post) or 
                                        <code className="ml-1 px-1 py-0.5 bg-gray-100 rounded">https://www.instagram.com/reel/ABC123xyz/</code> (reel)
                                        <br />
                                        <span className="text-xs text-gray-400 mt-1 block">
                                            💡 Tip: You can also paste the embed code directly if the URL doesn't work
                                        </span>
                                    </span>
                                </p>
                                
                                <div className="space-y-4">
                                    {instagramPosts.map((post, index) => (
                                        <div key={index}>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Post {index + 1}
                                            </label>
                                            <textarea
                                                value={post}
                                                onChange={(e) => handleInstagramPostChange(index, e.target.value)}
                                                placeholder="Paste Instagram URL (e.g., https://www.instagram.com/reel/ABC123xyz/) or embed code"
                                                rows={post && post.includes('<blockquote') ? 8 : 1}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-mono text-sm resize-y"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                                <button
                                    onClick={() => navigate('/admin/dashboard')}
                                    className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {saving ? (
                                        <>
                                            <LoadingSpinner />
                                            Saving...
                                        </>
                                    ) : (
                                        'Save Settings'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SettingsPage;

